/**
 * First-person walk controls.
 *
 * Deliberately a walk and not an orbit camera. The macroscopic half of the
 * curriculum is about protocol and spatial reasoning - where the aisle is, how
 * far the tool is from the gas cabinet, what you can see from the HMI - and
 * that only lands if the student moves through the space at human scale and
 * human height.
 *
 * THE MOUSE IS A CURSOR, NOT A CAMERA. This is a tool-operation environment
 * before it is a game: the student's pointer has to stay available for clicking
 * load ports, HMI screens and gas cabinets. Pointer lock takes the cursor away
 * and turns every stray hand movement into a camera swing, which is exactly
 * wrong here. So looking around is an explicit act - drag with the right button,
 * or turn with the arrow keys - and the cursor is never captured.
 */

import * as THREE from 'three'
import { clamp } from '../core/math'
import { isTypingTarget } from '../core/typing'
import { FAB } from './layout'
import {
  EYE_HEIGHT,
  resolveCollisions as resolveWalkCollisions,
  type Collider,
} from './walkPhysics'

// Re-exported because the walls belong to the walk from the outside - the
// scene's collider list and the headless subfab harness both name these, and
// both predate the physics moving into walkPhysics.ts.
export { EYE_HEIGHT, PLAYER_RADIUS } from './walkPhysics'
export type { Collider } from './walkPhysics'

const WALK_SPEED = 3.4
const RUN_SPEED = 6.2
const ACCEL = 14
/** Radians per pixel while dragging to look. */
const DRAG_SENSITIVITY = 0.0032
/** Radians per second when turning with the arrow keys. */
const TURN_SPEED = 1.9
/** Pointer travel, in pixels, beyond which a press counts as a drag not a click. */
const CLICK_SLOP = 5

/** The keys that mean "I want to move" - the ones that cancel a glide. */
const MOVE_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
])

export class Player {
  readonly camera: THREE.PerspectiveCamera
  readonly yawObject = new THREE.Object3D()
  private pitch = 0
  private yaw = 0

  private velocity = new THREE.Vector3()
  private keys = new Set<string>()
  private colliders: Collider[] = []

  /**
   * Walkable ground height at a point, set by main once the fab exists.
   * The third argument is the CURRENT ground height - over the sub-fab the
   * same (x, z) has two walkable surfaces, and which one you are on is a fact
   * about you rather than about the point.
   */
  groundAt: (x: number, z: number, foot: number) => number = () => 0
  /** The ground currently underfoot. Eye height rides on top of this. */
  private foot = 0

  /**
   * Tell the walk controller where the ground is when something else has been
   * moving the rig - VR locomotion tracks the ground itself, and without this
   * a student who took the headset off at the bottom of the stair would pop
   * back up through the ceiling on their first desktop step.
   */
  setFoot(foot: number): void {
    this.foot = foot
  }

  /** True while the student is dragging to look around. */
  dragging = false
  /** Set false while a modal panel owns the input. */
  enabled = true

  /** Camera tween used when focusing on a tool HMI. */
  private focus: {
    from: THREE.Vector3
    to: THREE.Vector3
    fromYaw: number
    toYaw: number
    fromPitch: number
    toPitch: number
    t: number
    duration: number
  } | null = null

  /** The surface events are read from; set by bindInput. */
  private domElement: HTMLElement | null = null
  /** One switch that removes every listener bindInput added. */
  private inputAbort: AbortController | null = null

  constructor() {
    this.camera = new THREE.PerspectiveCamera(68, 1, 0.05, 220)
    // Just inside the gowning airlock, which is where the splash screen says
    // you are. The hall runs the length of the process, so this is the start of
    // the flow rather than the middle of it.
    this.yawObject.position.set(-56, EYE_HEIGHT, 0)
    this.yawObject.add(this.camera)
    this.yaw = -Math.PI / 2
  }

  get position(): THREE.Vector3 {
    return this.yawObject.position
  }

  /** How fast the student is walking, metres per second. */
  get speed(): number {
    return this.velocity.length()
  }

  setColliders(colliders: Collider[]): void {
    this.colliders = colliders
  }

  /** Pointer position in normalised device coordinates, for picking. */
  readonly pointer = new THREE.Vector2(0, 0)
  /** True when the pointer is over the viewport at all. */
  pointerInside = false

  private lastX = 0
  private lastY = 0
  private pressX = 0
  private pressY = 0
  private pressButton = -1
  private pressWasTouch = false
  /** Id of the pointer that owns the look drag, so a second finger cannot steal it. */
  private dragPointer = -1

  /**
   * Set by main to handle a genuine click (as opposed to the end of a drag).
   * `touch` distinguishes a finger tap: main treats an out-of-reach tap as a
   * travel request, which a mouse user never needs - they have a keyboard.
   */
  onClick: ((ndc: THREE.Vector2, opts: { touch: boolean }) => void) | null = null

  /**
   * Start reading input from a rendering surface. Separate from construction
   * so the Player can exist before the canvas does (and, under React, so the
   * binding's lifetime can follow the canvas's mount rather than the
   * session). `dispose` undoes everything this adds.
   */
  bindInput(dom: HTMLElement): void {
    this.dispose()
    this.domElement = dom
    const abort = new AbortController()
    this.inputAbort = abort
    const signal = abort.signal

    // Right-drag is the look gesture, so the context menu has to go.
    dom.addEventListener('contextmenu', (e) => e.preventDefault(), { signal })

    dom.addEventListener('pointerdown', (e) => {
      this.pressX = e.clientX
      this.pressY = e.clientY
      this.pressButton = e.button
      this.pressWasTouch = e.pointerType === 'touch'
      if (!this.enabled) return
      // Right or middle button looks around; left stays free for interaction.
      // A finger has no second button, so on touch the one gesture is both:
      // CLICK_SLOP already separates a tap from a drag, which is exactly the
      // press-that-did-not-travel rule the mouse click path uses.
      const looks = e.button === 2 || e.button === 1 || (e.pointerType === 'touch' && !this.dragging)
      if (looks) {
        // Starting to look is a statement about where to point the eye, and an
        // in-flight glide is an older one - the newer instruction wins. Without
        // this the drag is silently ignored until the tween lands (see the
        // focus guard in pointermove), which reads as a dead mouse.
        this.focus = null
        this.dragging = true
        this.dragPointer = e.pointerId
        this.lastX = e.clientX
        this.lastY = e.clientY
        try {
          // Capture keeps the drag alive if the cursor leaves the canvas. It
          // throws if the pointer is already gone, which costs nothing here -
          // the drag simply stays bound to the canvas.
          dom.setPointerCapture(e.pointerId)
        } catch {
          /* drag continues without capture */
        }
        e.preventDefault()
      }
    }, { signal })

    const endDrag = (e: PointerEvent) => {
      // Only the finger that owns the drag can end it - lifting a second,
      // idle finger must not cut a look short.
      if (this.dragging && e.pointerId === this.dragPointer) {
        this.dragging = false
        this.dragPointer = -1
        try {
          dom.releasePointerCapture(e.pointerId)
        } catch {
          /* capture may already be gone */
        }
      }
    }

    dom.addEventListener('pointerup', (e) => {
      const moved = Math.hypot(e.clientX - this.pressX, e.clientY - this.pressY)
      const wasLeft = this.pressButton === 0
      endDrag(e)
      // A left press that did not travel is a click on whatever is under the
      // cursor. Anything that travelled was a drag, and must not also select.
      if (wasLeft && moved <= CLICK_SLOP && this.enabled) {
        this.updatePointer(e)
        this.onClick?.(this.pointer, { touch: this.pressWasTouch })
      }
      this.pressButton = -1
    }, { signal })

    dom.addEventListener('pointercancel', endDrag, { signal })

    dom.addEventListener('pointermove', (e) => {
      this.updatePointer(e)
      if (!this.dragging || !this.enabled || this.focus) return
      if (e.pointerId !== this.dragPointer) return
      const dx = e.clientX - this.lastX
      const dy = e.clientY - this.lastY
      this.lastX = e.clientX
      this.lastY = e.clientY
      this.yaw -= dx * DRAG_SENSITIVITY
      this.pitch = clamp(this.pitch - dy * DRAG_SENSITIVITY, -Math.PI / 2.2, Math.PI / 2.2)
    }, { signal })

    dom.addEventListener('pointerenter', () => {
      this.pointerInside = true
    }, { signal })
    dom.addEventListener('pointerleave', () => {
      this.pointerInside = false
    }, { signal })

    window.addEventListener('keydown', (e) => {
      // Never swallow keys aimed at a text field or a picker - the assistant
      // has one, and Ant Design renders more of them as div composites than a
      // tag test can see. One predicate, shared with the app shell.
      if (isTypingTarget(e.target)) return
      this.keys.add(e.code)
      // A movement key cancels an in-flight glide the same way a drag does:
      // otherwise WASD is dead for up to 2.4 s while a travel tween lands,
      // which is indistinguishable from a hang. Only while the student has
      // the floor - the glide into an opening tool panel keeps its keys.
      if (this.enabled && this.focus && MOVE_KEYS.has(e.code)) this.focus = null
      // Space and the arrows would otherwise scroll the page behind the canvas.
      if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault()
    }, { signal })
    window.addEventListener('keyup', (e) => this.keys.delete(e.code), { signal })
    window.addEventListener(
      'blur',
      () => {
        this.keys.clear()
        this.dragging = false
      },
      { signal },
    )
  }

  /** Remove every listener bindInput added and drop held input state. */
  dispose(): void {
    this.inputAbort?.abort()
    this.inputAbort = null
    this.domElement = null
    this.keys.clear()
    this.dragging = false
    this.dragPointer = -1
  }

  private updatePointer(e: PointerEvent): void {
    if (!this.domElement) return
    const rect = this.domElement.getBoundingClientRect()
    this.pointer.set(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    )
  }

  /** Smoothly move the eye to a viewpoint - used when opening a tool HMI. */
  /**
   * Stand the player somewhere, instantly - a restored session, not a glide.
   *
   * This exists because yaw and pitch are PRIVATE state stamped onto the
   * objects every update: writing `yawObject.rotation.y` from outside works
   * for exactly one frame and is then silently overwritten, which is the kind
   * of bug that looks like it works in a screenshot. Any in-flight focus tween
   * is cancelled - the pose is the newer instruction.
   */
  setPose(position: THREE.Vector3, yaw: number, pitch: number): void {
    this.yawObject.position.copy(position)
    // The pose's eye height IS ground plus eye, so the ground comes with it.
    // Without this, a place restored in the sub-fab would carry foot = 0 and
    // the first step would snap the student up through the ceiling.
    this.foot = position.y - EYE_HEIGHT
    this.yaw = yaw
    this.pitch = clamp(pitch, -Math.PI / 2.2, Math.PI / 2.2)
    this.focus = null
    this.yawObject.rotation.y = this.yaw
    this.camera.rotation.x = this.pitch
  }

  focusOn(target: THREE.Vector3, lookAt: THREE.Vector3, duration = 0.8): void {
    const dir = new THREE.Vector3().subVectors(lookAt, target)
    const toYaw = Math.atan2(-dir.x, -dir.z)
    const toPitch = Math.atan2(dir.y, Math.hypot(dir.x, dir.z))
    this.focus = {
      from: this.yawObject.position.clone(),
      to: target.clone(),
      fromYaw: this.yaw,
      toYaw: this.shortestYaw(this.yaw, toYaw),
      fromPitch: this.pitch,
      toPitch,
      t: 0,
      duration,
    }
  }

  /** Pick the equivalent target angle that does not spin the long way round. */
  private shortestYaw(from: number, to: number): number {
    let delta = (to - from) % (Math.PI * 2)
    if (delta > Math.PI) delta -= Math.PI * 2
    if (delta < -Math.PI) delta += Math.PI * 2
    return from + delta
  }

  /** Seconds of scripted drift served behind the entry screen. */
  private driftT = 0

  /**
   * Back to the top of the entry dolly, for a student returning to the
   * airlock from inside. The next idleDrift frame stands them at its start;
   * any focus tween in flight is dropped, since the drift owns the camera
   * from here.
   */
  restartDrift(): void {
    this.driftT = 0
    this.focus = null
    this.velocity.set(0, 0, 0)
    this.foot = 0
  }

  /**
   * Where the entry dolly ends: just short of the first bay, facing down the
   * aisle. For a student who walks in without the entry screen - having
   * chosen this fab from the other fab's airlock - so they stand where a
   * student who read the screen would have.
   */
  standAtEntry(): void {
    // Where the entry dolly ends in the authored halls - and, in a hall too
    // short to have that much run-in (a floor somebody built, fab/floorPlan.ts),
    // a dozen metres in from the end wall: past the airlock and the gowning
    // station, short of the first bay, facing up the aisle.
    const x = Math.max(-16.5, -FAB.length / 2 + 12)
    this.setPose(new THREE.Vector3(x, EYE_HEIGHT, -1.7), -Math.PI / 2, -0.025)
  }

  /**
   * Slow push up the aisle, run while the entry screen is still up.
   *
   * A frozen frame behind the splash makes the fab look like a photograph of a
   * fab. The dolly eases out rather than running on, so however long a student
   * spends reading the screen they arrive at the same spot, still short of the
   * first bay and still facing down the aisle.
   */
  idleDrift(dt: number): void {
    this.driftT += dt
    const t = this.driftT
    const dolly = 3.9 * (1 - Math.exp(-t * 0.14))
    // Stand off the aisle centreline: the entry screen's scrim clears toward
    // the right of the frame, which is the +Z bay, so the tool that shows
    // through is a tool and not the far wall.
    this.yawObject.position.set(
      -20.4 + dolly,
      EYE_HEIGHT + Math.sin(t * 0.55) * 0.018,
      -1.7 + Math.sin(t * 0.15) * 0.4,
    )
    this.yaw = -Math.PI / 2 + Math.sin(t * 0.1) * 0.075
    this.pitch = -0.025 + Math.sin(t * 0.17) * 0.012
    this.applyRotation()
  }

  update(dt: number): void {
    if (this.focus) {
      const f = this.focus
      f.t = Math.min(f.t + dt / f.duration, 1)
      // Smootherstep for a settle that does not overshoot.
      const e = f.t * f.t * f.t * (f.t * (f.t * 6 - 15) + 10)
      this.yawObject.position.lerpVectors(f.from, f.to, e)
      this.yaw = f.fromYaw + (f.toYaw - f.fromYaw) * e
      this.pitch = f.fromPitch + (f.toPitch - f.fromPitch) * e
      if (f.t >= 1) {
        this.focus = null
        // A focus glide can carry the student between levels (every HMI
        // viewpoint is on the fab floor); resync the ground from where the
        // glide actually landed, or the next step would snap back down.
        const p = this.yawObject.position
        this.foot = this.groundAt(p.x, p.z, p.y - EYE_HEIGHT)
      }
      this.applyRotation()
      return
    }

    if (this.enabled) this.move(dt)
    this.applyRotation()
  }

  private move(dt: number): void {
    const forward =
      Number(this.keys.has('KeyW') || this.keys.has('ArrowUp')) -
      Number(this.keys.has('KeyS') || this.keys.has('ArrowDown'))
    const strafe = Number(this.keys.has('KeyD')) - Number(this.keys.has('KeyA'))

    // Left and right arrows turn rather than strafe: they are the alternative
    // to drag-look for anyone who would rather not hold a mouse button, and a
    // student on a trackpad has no comfortable right-drag at all.
    const turn = Number(this.keys.has('ArrowLeft')) - Number(this.keys.has('ArrowRight'))
    if (turn !== 0 && !this.focus) this.yaw += turn * TURN_SPEED * dt

    const speed = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') ? RUN_SPEED : WALK_SPEED

    // Desired velocity in world space from the yaw basis.
    const sin = Math.sin(this.yaw)
    const cos = Math.cos(this.yaw)
    const desired = new THREE.Vector3(
      (-sin * forward + cos * strafe),
      0,
      (-cos * forward - sin * strafe),
    )
    if (desired.lengthSq() > 0) desired.normalize().multiplyScalar(speed)

    // Exponential approach gives weight without a physics step.
    const blend = 1 - Math.exp(-ACCEL * dt)
    this.velocity.lerp(desired, blend)

    const next = this.yawObject.position.clone().addScaledVector(this.velocity, dt)
    this.resolveCollisions(next)
    // Follow the ground. On the flat this is a constant; on the stair it is a
    // ramp, which reads as descending steps at walking speed without any of
    // the stepped-collision machinery a real character controller would need.
    this.foot = this.groundAt(next.x, next.z, this.foot)
    this.yawObject.position.copy(next)
    this.yawObject.position.y = this.foot + EYE_HEIGHT
  }

  /**
   * Push the player out of any collider it has entered, resolving along the
   * shallowest axis. Crude compared to a real character controller, but for a
   * room of axis-aligned boxes it never lets you through a wall and never
   * catches on a corner.
   */
  /**
   * Push a position out of any collider it has entered.
   *
   * Public because the VR rig walks the same floor and has to hit the same
   * walls - a tool you can stroll through in a headset teaches the wrong thing
   * about where a tool is.
   */
  resolveCollisions(pos: THREE.Vector3, foot: number = this.foot): void {
    resolveWalkCollisions(pos, foot, this.colliders, this.velocity)
  }

  private applyRotation(): void {
    this.yawObject.rotation.y = this.yaw
    this.camera.rotation.x = this.pitch
  }

  /** World-space forward ray, for picking what the student is looking at. */
  forwardRay(out: THREE.Ray): THREE.Ray {
    out.origin.copy(this.camera.getWorldPosition(new THREE.Vector3()))
    out.direction.copy(this.camera.getWorldDirection(new THREE.Vector3()))
    return out
  }
}
